import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { certificatesAPI } from '../../api';
import ClientPicker from '../../components/ClientPicker';
import Layout from '../../components/Layout';
import { formatDateForInput } from '../../lib/utils';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { FormLabel } from '../../components/ui/FormLabel';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';

const CertificateEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;
  const certificateType = searchParams.get('type') || 'eic';

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [certificateStatus, setCertificateStatus] = useState(null);

  // Certificate form state
  const [formData, setFormData] = useState({
    clientId: '',
    certificateNum: '',
    certificateType: certificateType,
    jobReference: '',
    jobDescription: '',
    dateCreated: new Date().toISOString().split('T')[0],
    dateIssued: '',
    dateCompleted: '',
    installationAddress: '',
    installationType: '',
    notes: '',
  });

  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [certificateData, setCertificateData] = useState({});

  useEffect(() => {
    if (isEditMode) {
      fetchCertificate();
    } else {
      // Generate next certificate number based on type
      const timestamp = Date.now().toString().slice(-6);
      const prefix = certificateType.toUpperCase();
      setFormData(prev => ({
        ...prev,
        certificateNum: `${prefix}-${timestamp}`,
        certificateType: certificateType,
      }));
    }
  }, [id, certificateType]);

  const fetchCertificate = async () => {
    try {
      setLoading(true);
      setError('');
      const certificate = await certificatesAPI.getOne(id);

      setFormData({
        clientId: certificate.client.id,
        certificateNum: certificate.certificateNum,
        certificateType: certificate.certificateType,
        jobReference: certificate.jobReference || '',
        jobDescription: certificate.jobDescription || '',
        dateCreated: formatDateForInput(certificate.dateCreated) || new Date().toISOString().split('T')[0],
        dateIssued: formatDateForInput(certificate.dateIssued) || '',
        dateCompleted: formatDateForInput(certificate.dateCompleted) || '',
        installationAddress: certificate.installationAddress || '',
        installationType: certificate.installationType || '',
        notes: certificate.notes || '',
      });

      setCertificateStatus(certificate.status);
      setSelectedClient(certificate.client);
      setCertificateData(certificate.data || {});
    } catch (err) {
      setError(err.message || 'Failed to load certificate');
      console.error('Error fetching certificate:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({ ...prev, clientId: client.id }));
    setShowClientPicker(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.clientId) {
      setError('Please select a client');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        ...formData,
        data: certificateData,
      };

      if (isEditMode) {
        await certificatesAPI.update(id, payload);
        alert('Certificate updated successfully');
      } else {
        await certificatesAPI.create(payload);
        alert('Certificate created successfully');
      }

      navigate('/tenant/certification');
    } catch (err) {
      setError(err.message || `Failed to ${isEditMode ? 'update' : 'create'} certificate`);
      console.error('Save error:', err);
    } finally {
      setSaving(false);
    }
  };

  const getCertificateTypeName = () => {
    const names = {
      eic: 'Electrical Installation Certificate (EIC)',
      eicr: 'Electrical Installation Condition Report (EICR)',
      mewc: 'Minor Electrical Works Certificate (MEWC)',
    };
    return names[formData.certificateType] || formData.certificateType.toUpperCase();
  };

  const renderCertificateForm = () => {
    // Placeholder forms for each certificate type
    switch (formData.certificateType) {
      case 'eic':
        return (
          <Card>
            <CardContent className="p-6">
            <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-4">
              EIC Form - Placeholder
            </h2>
            <p className="text-xs text-[var(--ds-text-secondary)] mb-4">
              The detailed EIC form will be implemented here. This will include:
            </p>
            <ul className="list-disc list-inside text-xs text-[var(--ds-text-secondary)] space-y-2 mb-4">
              <li>Installation details and characteristics</li>
              <li>Schedule of inspections</li>
              <li>Schedule of test results</li>
              <li>Electrical installation certificate declaration</li>
              <li>BS7671 compliance information</li>
            </ul>
            <div className="border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] p-4 bg-[var(--ds-surface-panel2)]">
              <p className="text-xs text-[var(--ds-text-muted)]">
                Certificate data will be stored in the JSONB data field.
              </p>
            </div>
            </CardContent>
          </Card>
        );

      case 'eicr':
        return (
          <Card>
            <CardContent className="p-6">
            <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-4">
              EICR Form - Placeholder
            </h2>
            <p className="text-xs text-[var(--ds-text-secondary)] mb-4">
              The detailed EICR form will be implemented here. This will include:
            </p>
            <ul className="list-disc list-inside text-xs text-[var(--ds-text-secondary)] space-y-2 mb-4">
              <li>Condition report details</li>
              <li>Summary of inspection</li>
              <li>Extent and limitations of inspection</li>
              <li>Schedule of inspections</li>
              <li>Schedule of test results</li>
              <li>Observation and recommendations</li>
              <li>Overall assessment (Satisfactory / Unsatisfactory)</li>
            </ul>
            <div className="border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] p-4 bg-[var(--ds-surface-panel2)]">
              <p className="text-xs text-[var(--ds-text-muted)]">
                Certificate data will be stored in the JSONB data field.
              </p>
            </div>
            </CardContent>
          </Card>
        );

      case 'mewc':
        return (
          <Card>
            <CardContent className="p-6">
            <h2 className="text-h2 font-semibold text-[var(--ds-text-primary)] mb-4">
              MEWC Form - Placeholder
            </h2>
            <p className="text-xs text-[var(--ds-text-secondary)] mb-4">
              The detailed MEWC form will be implemented here. This will include:
            </p>
            <ul className="list-disc list-inside text-xs text-[var(--ds-text-secondary)] space-y-2 mb-4">
              <li>Description of minor works</li>
              <li>Installation details</li>
              <li>Inspection results</li>
              <li>Test results</li>
              <li>Declaration of compliance</li>
            </ul>
            <div className="border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] p-4 bg-[var(--ds-surface-panel2)]">
              <p className="text-xs text-[var(--ds-text-muted)]">
                Certificate data will be stored in the JSONB data field.
              </p>
            </div>
            </CardContent>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/tenant/certification')}
            className="mb-4 text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
          >
            ← Back to Certificates
          </Button>
          <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
            {isEditMode ? 'Edit Certificate' : 'New Certificate'}
          </h1>
          <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
            {getCertificateTypeName()}
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)]">
            <p className="text-xs text-[var(--ds-status-danger)]">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--ds-accent-primary)]"></div>
            <p className="mt-2 text-xs text-[var(--ds-text-muted)]">Loading certificate...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-h2 font-semibold text-[var(--ds-text-primary)]">
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Select
                    label="Certificate Type"
                    value={formData.certificateType}
                    onChange={(e) => setFormData(prev => ({ ...prev, certificateType: e.target.value }))}
                    disabled={isEditMode}
                    className="h-8"
                  >
                    <option value="eic">EIC - Electrical Installation Certificate</option>
                    <option value="eicr">EICR - Electrical Installation Condition Report</option>
                    <option value="mewc">MEWC - Minor Electrical Works Certificate</option>
                  </Select>
                </div>

                <div>
                  <Input
                    label="Certificate Number"
                    type="text"
                    required
                    value={formData.certificateNum}
                    onChange={(e) => setFormData(prev => ({ ...prev, certificateNum: e.target.value }))}
                    requiredIndicator
                    className="h-8"
                  />
                </div>

                <div className="md:col-span-2">
                  <FormLabel required>
                    Client
                  </FormLabel>
                  {selectedClient ? (
                    <div className="flex items-center justify-between p-3 border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] bg-[var(--ds-surface-panel)]">
                      <span className="text-xs text-[var(--ds-text-primary)]">
                        {selectedClient.clientType === 'company'
                          ? selectedClient.companyName
                          : `${selectedClient.firstName} ${selectedClient.lastName}`}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        type="button"
                        onClick={() => setShowClientPicker(true)}
                        className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      type="button"
                      onClick={() => setShowClientPicker(true)}
                      className="w-full justify-start"
                    >
                      Select Client
                    </Button>
                  )}
                </div>

                <div>
                  <Input
                    label="Date Created"
                    type="date"
                    value={formatDateForInput(formData.dateCreated)}
                    onChange={(e) => setFormData(prev => ({ ...prev, dateCreated: e.target.value }))}
                    className="h-8"
                  />
                </div>

                <div>
                  <Input
                    label="Date Issued"
                    type="date"
                    value={formatDateForInput(formData.dateIssued)}
                    onChange={(e) => setFormData(prev => ({ ...prev, dateIssued: e.target.value }))}
                    className="h-8"
                  />
                </div>

                <div>
                  <Input
                    label="Date Completed"
                    type="date"
                    value={formatDateForInput(formData.dateCompleted)}
                    onChange={(e) => setFormData(prev => ({ ...prev, dateCompleted: e.target.value }))}
                    className="h-8"
                  />
                </div>

                <div>
                  <Input
                    label="Job Reference"
                    type="text"
                    value={formData.jobReference}
                    onChange={(e) => setFormData(prev => ({ ...prev, jobReference: e.target.value }))}
                    className="h-8"
                  />
                </div>

                <div className="md:col-span-2">
                  <Textarea
                    label="Job Description"
                    value={formData.jobDescription}
                    onChange={(e) => setFormData(prev => ({ ...prev, jobDescription: e.target.value }))}
                    rows={3}
                    className="text-xs"
                  />
                </div>

                <div className="md:col-span-2">
                  <Textarea
                    label="Installation Address"
                    value={formData.installationAddress}
                    onChange={(e) => setFormData(prev => ({ ...prev, installationAddress: e.target.value }))}
                    rows={2}
                    className="text-xs"
                  />
                </div>

                <div>
                  <Input
                    label="Installation Type"
                    type="text"
                    value={formData.installationType}
                    onChange={(e) => setFormData(prev => ({ ...prev, installationType: e.target.value }))}
                    placeholder="e.g. Domestic, Commercial, Industrial"
                    className="h-8"
                  />
                </div>

                <div className="md:col-span-2">
                  <Textarea
                    label="Notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    rows={3}
                    className="text-xs"
                  />
                </div>
              </div>
              </CardContent>
            </Card>

            {/* Certificate-Specific Form */}
            {renderCertificateForm()}

            {/* Actions */}
            <div className="flex justify-end space-x-4">
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={() => navigate('/tenant/certification')}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={saving}
              >
                {saving ? 'Saving...' : isEditMode ? 'Update Certificate' : 'Create Certificate'}
              </Button>
            </div>
          </form>
        )}
      </div>

      {/* Client Picker Modal */}
      {showClientPicker && (
        <ClientPicker
          onSelect={handleClientSelect}
          onClose={() => setShowClientPicker(false)}
        />
      )}
    </Layout>
  );
};

export default CertificateEditor;

